import { useState } from 'react';
import * as React from 'react';
import { useMutation } from '@apollo/client';
import {
  addPaymentMethodMutation,
  addPaymentMethodMutationVariables,
  addPaymentMethodMutation_addPaymentMethod_SetupSessionStripe
} from '../../core-types';
import { useRouter } from 'next/router';
import { Loading, LoadableButton, Alert } from '../../components';
import { FormattedMessage } from 'react-intl';
import { GraphQLErrors } from '../../components/GraphQLErrors';
import { useCurrentUser } from '../../lib/core/useCurrentUser';
import { getQueryString } from '../../lib/utils';
import { usePaymentSetupCompletion } from '../AsyncProcessCompletion';
import { StripeCheckoutRedirect } from '../StripeCheckoutRedirect';
import { globalMessages } from '../../lib/messages';
import { ADD_PAYMENT_METHOD_MUTATION } from './graphql';
import { useToasts } from '../../components/Toasts';

export function AddOrReplacePaymentMethod({
  children
}: {
  children: (props: {
    paymentMethodSuccess: boolean;
    addOrReplacePaymentMethodUI: React.ReactElement;
    onAddPaymentMethodClick: () => void;
  }) => React.ReactElement;
}) {
  const router = useRouter();
  const [setupSessionStripe, setSetupSessionStripe] = useState<
    addPaymentMethodMutation_addPaymentMethod_SetupSessionStripe | undefined
  >();
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const { currentUser, refetch } = useCurrentUser();

  const [addPaymentMethod, addPaymentMethodResult] = useMutation<
    addPaymentMethodMutation,
    addPaymentMethodMutationVariables
  >(ADD_PAYMENT_METHOD_MUTATION, {
    onCompleted: data => {
      setRedirecting(true);
      switch (data.addPaymentMethod.__typename) {
        case 'SetupSessionStripe': {
          setSetupSessionStripe(data.addPaymentMethod);
          break;
        }
        case 'SetupSessionPayex':
        case 'SetupSessionNevogate': {
          window.location.replace(data.addPaymentMethod.redirectUrl);
          break;
        }
      }
    },
    onError: () => {
      setRedirecting(false);
    }
  });

  const { addToast } = useToasts();

  const paymentMethod = currentUser?.paymentMethod;
  const error = addPaymentMethodResult.error;

  function onAddPaymentMethodClick() {
    setRedirecting(false);
    addPaymentMethod({
      variables: {
        paths: {
          successPath: router.asPath,
          cancelPath: router.asPath
        }
      }
    });
  }

  const setupToken = getQueryString(router.query.setupToken);
  const asyncProcessCompletionResult = usePaymentSetupCompletion({
    setupToken,
    paymentState: 'success',
    skip: !setupToken,
    onAsyncProcessCompleted: state => {
      switch (state) {
        case 'success': {
          addToast({
            text: (
              <FormattedMessage
                id="paymentCard.addSuccess"
                defaultMessage="Updated payment card"
              />
            ),
            type: 'success'
          });
          // refetch currentUser
          refetch();
          break;
        }
        case 'cancelled': {
          // do nothing
          break;
        }
        case 'failed':
        case 'error': {
          // error is shown in the UI
          break;
        }
      }
    }
  });

  return children({
    paymentMethodSuccess:
      asyncProcessCompletionResult.postMessageState === 'success',
    onAddPaymentMethodClick,
    addOrReplacePaymentMethodUI: (
      <>
        {setupSessionStripe && (
          <StripeCheckoutRedirect
            sessionId={setupSessionStripe.sessionId}
            publishableKey={setupSessionStripe.publishableKey}
            showSpinner={false}
          />
        )}
        {asyncProcessCompletionResult.loading && (
          <Loading>
            <FormattedMessage
              id="paymentCard.verifying"
              defaultMessage="Verifying payment card"
            />
          </Loading>
        )}
        {(asyncProcessCompletionResult.postMessageState === 'error' ||
          asyncProcessCompletionResult.postMessageState === 'failed') && (
          <Alert variant="error">
            <FormattedMessage
              id="paymentCard.verifyError"
              defaultMessage="Could not verify payment card."
            />
          </Alert>
        )}
        {!asyncProcessCompletionResult.loading && (
          <>
            {error && <GraphQLErrors error={error} />}
            <LoadableButton
              loading={addPaymentMethodResult.loading || redirecting}
              responseState={addPaymentMethodResult.error ? 'error' : 'success'}
              shape="cool"
              variant="primary"
              onClick={onAddPaymentMethodClick}
            >
              {!paymentMethod ? (
                <FormattedMessage {...globalMessages.addCard} />
              ) : (
                <FormattedMessage
                  id="paymentCard.replace"
                  defaultMessage="Replace card"
                />
              )}
            </LoadableButton>
          </>
        )}
      </>
    )
  });
}
